/**
 * Shared distance-radius helpers for surfacing nearby store locations
 * (store locator + "browse all locations") sorted closest-first.
 */

/** Default radius used to decide which chain locations count as "nearby". */
export const NEARBY_RADIUS_MILES = 100;

/** Matches the meters-per-mile conversion already used for dispensary distance elsewhere. */
export const METERS_PER_MILE = 1609.34;

export function metersToMiles(meters: number): number {
  return meters / METERS_PER_MILE;
}

export function milesToMeters(miles: number): number {
  return miles * METERS_PER_MILE;
}

type DispensaryWithDistance = {
  distance?: number | null;
};

type WithNumericDistance<T> = T & { distance: number };

function hasDistanceWithinRadius<T extends DispensaryWithDistance>(
  dispensary: T | null | undefined,
  radiusMiles: number
): dispensary is WithNumericDistance<T> {
  if (!dispensary || typeof dispensary.distance !== 'number') {
    return false;
  }
  return metersToMiles(dispensary.distance) <= radiusMiles;
}

/**
 * Filters to dispensaries within radiusMiles and sorts nearest-first.
 * `distance` is expected in meters, matching the GraphQL dispensary distance field.
 * Entries without a usable distance are dropped -- we can't vouch they're nearby.
 */
export function getNearbyDispensariesSortedByDistance<T extends DispensaryWithDistance>(
  dispensaries: Array<T | null | undefined> | null | undefined,
  radiusMiles: number = NEARBY_RADIUS_MILES
): Array<WithNumericDistance<T>> {
  if (!dispensaries?.length) {
    return [];
  }

  return dispensaries
    .filter((dispensary): dispensary is WithNumericDistance<T> => hasDistanceWithinRadius(dispensary, radiusMiles))
    .sort((a, b) => a.distance - b.distance);
}
